import { Suspense, useRef } from 'react'
import { Await, useLoaderData } from 'react-router-typesafe'
import LoadingPanel from '@/components/loading-panel/LoadingPanel'
import { useFetcher } from 'react-router-dom'
import { isUserRoleAllowed } from '@/routes/utils'
import CustomizationEditorForm from '@/routes/customizations/CustomizationEditor.form'
import { CustomizationEditorProps } from '@/routes/customizations/CustomizationEditor.types'
import { customizationEditorRouteLoader } from '@/routes/customizations/CustomizationEditor.route'
import { Form as DXForm, GroupItem } from 'devextreme-react/form'
import AttachmentsManager from '@/components/file-manager/AttachmentsManager'
import { FileManagerRef } from 'devextreme-react/cjs/file-manager'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { DocumentPermissions } from '@/enums'

const CustomizationEditor = (props: CustomizationEditorProps) => {
  const { creating } = props
  const { title, customization, getAziende, getQsImpiegati, getUserInfo, getAnalisi, defaultCRUDAllowedRoles } =
    useLoaderData<typeof customizationEditorRouteLoader>()

  const fetcher = useFetcher()
  const fileManagerRef = useRef<FileManagerRef>(null)
  const { client } = useQsAdminApiManager()

  return (
    <>
      <Suspense fallback={<LoadingPanel />}>
        <Await resolve={Promise.all([getAziende, getQsImpiegati, getUserInfo, getAnalisi])}>
          {([aziende, impiegati, userInfo, analisi]) => (
            <>
              <h2>{title}</h2>
              <div id="editor-customization-container" className={`dx-card responsive-paddings`}>
                <fetcher.Form>
                  <CustomizationEditorForm
                    customization={customization}
                    creating={creating}
                    aziende={aziende.data.value}
                    isRoleAllowed={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
                    userInfo={userInfo}
                    fetcher={fetcher}
                    impiegati={impiegati.data.value}
                    analisi={analisi?.data.value}
                  ></CustomizationEditorForm>
                </fetcher.Form>
              </div>
              {!creating && (
                <div id="allegati-personalizzazione-container" className={`dx-card responsive-paddings`}>
                  <DXForm id="form-allegati">
                    <GroupItem colCount={1} caption="Allegati">
                      <AttachmentsManager
                        fileManagerRef={fileManagerRef}
                        folderUrl={customization?.attachmentsFolderUrl}
                        preliminarPermissionsAction={async () => {
                          try {
                            const response = await client.post(
                              '/api/personalizzazione/drivepermissions',
                              {
                                objectId: customization.id,
                                path: customization.attachmentsFolderUrl,
                              },
                              {
                                headers: {
                                  'Content-Type': 'application/json',
                                },
                              },
                            )
                            return response.status === 200
                              ? (response.data as DocumentPermissions)
                              : DocumentPermissions.Deny
                          } catch (error) {
                            console.log('Errore in settaggio permessi sulla cartella allegati personalizzazione', error)
                            return DocumentPermissions.Deny
                          }
                        }}
                        createDirectoryAction={async (
                          rootFolderPath: string,
                          name: string,
                          conflictBehavior: string,
                        ) => {
                          return await client.post(
                            '/api/personalizzazione/createfolder',
                            {
                              rootFolder: rootFolderPath,
                              newFolderName: name,
                              conflictBehavior,
                              objectId: customization.id,
                            },
                            {
                              headers: {
                                'Content-Type': 'application/json',
                              },
                            },
                          )
                        }}
                      />
                    </GroupItem>
                  </DXForm>
                </div>
              )}
            </>
          )}
        </Await>
      </Suspense>
    </>
  )
}

export default CustomizationEditor
